import React, { useCallback, useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import {
  cancelSkillAgentSearch,
  clearSkillAgentSearchPhotos,
  deleteSkillAgentSearch,
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  getSkillAgentSearchesMeta,
  listSkillAgentSearchItems,
  runSkillAgentSearch,
  updateSkillAgentSearch,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { searchStatusLabel } from "./skillAgentTaskTree";

function SearchThumb({ item, onOpen }) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => src && onOpen?.(item)}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        cursor: src ? "zoom-in" : "default",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": src
          ? {
              borderColor: "primary.main",
              boxShadow: (t) =>
                `0 4px 14px ${alpha(t.palette.primary.main, 0.18)}`,
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          height: 140,
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onError={() => setFailed(true)}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary">
            Sin preview
          </Typography>
        ) : (
          <CircularProgress size={18} />
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" fontWeight={700} noWrap display="block">
          {item.Title || item.BrandName || "img"}
        </Typography>
      </Box>
    </Box>
  );
}

function SearchPhotoLightbox({ items, index, onClose, onIndexChange }) {
  const item = index != null && items[index] ? items[index] : null;
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item?.id) {
      setSrc("");
      return undefined;
    }
    let revoked = false;
    let objectUrl = "";
    setSrc("");
    setFailed(false);
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item?.id]);

  useEffect(() => {
    if (item == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onIndexChange?.(Math.max(0, index - 1));
      if (e.key === "ArrowRight")
        onIndexChange?.(Math.min(items.length - 1, index + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, index, items.length, onClose, onIndexChange]);

  if (!item) return null;

  const driveHref = item.DriveFileUrl || null;
  const sourceHref = item.SourceUrl || null;
  const canPrev = index > 0;
  const canNext = index < items.length - 1;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.paper",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            {item.Title || item.BrandName || "Foto"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[item.BrandName, `${index + 1} / ${items.length}`]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>
        {sourceHref ? (
          <Button
            size="small"
            component={Link}
            href={sourceHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Origen
          </Button>
        ) : null}
        {driveHref ? (
          <Button
            size="small"
            component={Link}
            href={driveHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Drive
          </Button>
        ) : null}
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: 0,
          position: "relative",
          bgcolor: alpha("#000", 0.92),
          minHeight: { xs: 320, md: 560 },
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: "70vh", md: "78vh" },
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : failed ? (
          <Typography color="grey.400">No se pudo cargar la foto</Typography>
        ) : (
          <CircularProgress sx={{ color: "grey.200" }} />
        )}
        <IconButton
          disabled={!canPrev}
          onClick={() => onIndexChange?.(index - 1)}
          sx={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label="Anterior"
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          disabled={!canNext}
          onClick={() => onIndexChange?.(index + 1)}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label="Siguiente"
        >
          <ChevronRightIcon />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
}

export default function SkillAgentSearchDetail({
  searchId,
  onChanged,
  onDeleted,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState(null);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [editForm, setEditForm] = useState({
    Titulo: "",
    Tipologia: "",
    brandIds: [],
    customUrls: "",
    maxImagesPerBrand: "3",
    maxAgeDays: "60",
  });

  const images = useMemo(
    () => (Array.isArray(items) ? items.filter((i) => i.Kind === "image") : []),
    [items],
  );

  const load = useCallback(async () => {
    if (!searchId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSkillAgentSearchById(searchId);
      setSearch(data);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setRunning(data?.Status === "running");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar el search.");
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getSkillAgentSearchesMeta()
      .then((m) => setCatalogBrands(Array.isArray(m?.brands) ? m.brands : []))
      .catch(() => setCatalogBrands([]));
  }, []);

  useEffect(() => {
    if (!searchId || !running) return undefined;
    const t = setInterval(async () => {
      try {
        const data = await listSkillAgentSearchItems(searchId);
        if (data?.status && data.status !== "running") {
          setRunning(false);
          await load();
          onChanged?.();
          if (data.status === "done") {
            setOkMsg(
              data.items?.length
                ? "Search listo (fotos en Drive)."
                : "Search terminado.",
            );
          } else if (data.status === "error") {
            setError("Falló el search.");
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [searchId, running, load, onChanged]);

  const openEdit = () => {
    const c =
      search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
        ? search.ConstraintsJson
        : {};
    const brands = Array.isArray(c.researchBrands) ? c.researchBrands : [];
    const urls = Array.isArray(c.customUrls) ? c.customUrls.join("\n") : "";
    setEditForm({
      Titulo: search?.Titulo || "",
      Tipologia: search?.Tipologia || c.tipología || "",
      brandIds: brands.length ? brands : catalogBrands.map((b) => b.id),
      customUrls: urls,
      maxImagesPerBrand: String(c.maxImagesPerBrand || 3),
      maxAgeDays: String(c.maxAgeDays != null ? c.maxAgeDays : 60),
    });
    setEditOpen(true);
  };

  const handleSaveParams = async ({ andRun = false } = {}) => {
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const customUrls = String(editForm.customUrls || "")
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      const researchBrands = Array.isArray(editForm.brandIds)
        ? editForm.brandIds
        : [];
      if (!researchBrands.length && !customUrls.length) {
        setError("Marca al menos una marca o URL.");
        setBusy(false);
        return;
      }
      const prev =
        search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
          ? search.ConstraintsJson
          : {};
      const updated = await updateSkillAgentSearch(searchId, {
        Titulo: editForm.Titulo.trim() || search.Titulo,
        Tipologia: editForm.Tipologia || null,
        ConstraintsJson: {
          ...prev,
          tipología: editForm.Tipologia || "",
          researchBrands,
          customUrls,
          maxImagesPerBrand: Number(editForm.maxImagesPerBrand) || 3,
          maxAgeDays: Number(editForm.maxAgeDays) || 60,
          preferNewest: prev.preferNewest !== false,
          skipKnown: prev.skipKnown !== false,
        },
      });
      setSearch((p) => ({ ...p, ...updated }));
      setEditOpen(false);
      setOkMsg("Parámetros guardados.");
      onChanged?.();
      if (andRun) {
        await runSkillAgentSearch(searchId, {
          customerFolderName: search?.CustomerName || undefined,
        });
        setRunning(true);
        setSearch((p) => (p ? { ...p, Status: "running" } : p));
        setOkMsg("Relanzado con los nuevos parámetros…");
      }
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  };

  const handleRun = async () => {
    setError("");
    setOkMsg("");
    try {
      await runSkillAgentSearch(searchId, {
        customerFolderName: search?.CustomerName || undefined,
      });
      setRunning(true);
      setSearch((p) => (p ? { ...p, Status: "running" } : p));
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo lanzar.");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelSkillAgentSearch(searchId);
      setRunning(false);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cancelar.");
    }
  };

  const handleClearPhotos = async () => {
    setBusy(true);
    setError("");
    try {
      await clearSkillAgentSearchPhotos(searchId);
      setClearOpen(false);
      await load();
      onChanged?.();
      setOkMsg("Fotos borradas. Puedes cambiar parámetros y relanzar.");
    } catch (err) {
      setError(
        err?.response?.data?.error || "No se pudieron borrar las fotos.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await deleteSkillAgentSearch(searchId);
      setDeleteOpen(false);
      onDeleted?.(searchId);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo eliminar.");
      setBusy(false);
    }
  };

  if (loading && !search) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!search) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Search no encontrado.
      </Alert>
    );
  }

  const locked = running || busy;

  return (
    <Stack spacing={2.25} sx={{ p: { xs: 2, md: 2.5 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.35 }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
              noWrap
              sx={{ lineHeight: 1.25 }}
            >
              {search.Titulo}
            </Typography>
            <Chip
              size="small"
              label={searchStatusLabel(search.Status)}
              color={
                search.Status === "done"
                  ? "success"
                  : search.Status === "error"
                    ? "error"
                    : search.Status === "running"
                      ? "warning"
                      : "default"
              }
              sx={{
                height: 24,
                borderRadius: 1.25,
                fontWeight: 800,
                flexShrink: 0,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[search.CustomerName, search.Temporada, search.Tipologia]
              .filter(Boolean)
              .join(" · ") || "SEARCH library"}
          </Typography>
          {search.ResultSummary && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.35 }}
            >
              {search.ResultSummary}
            </Typography>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{
            flexShrink: 0,
            "& .MuiButton-root": {
              height: 34,
              minHeight: 34,
              py: 0,
              px: 1.35,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
            },
          }}
        >
          {search.DriveFolderUrl && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewOutlinedIcon />}
              href={search.DriveFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Drive
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            disabled={locked}
            onClick={openEdit}
          >
            Parámetros
          </Button>
          {running ? (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<StopOutlinedIcon />}
              onClick={handleCancel}
            >
              Cancelar
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={handleRun}
              disabled={locked}
              sx={{
                px: 1.75,
                boxShadow: (t) =>
                  `0 2px 10px ${alpha(t.palette.primary.main, 0.28)}`,
              }}
            >
              {images.length ? "Relanzar" : "Lanzar"}
            </Button>
          )}
          <Button
            size="small"
            color="error"
            variant="text"
            startIcon={<DeleteOutlineIcon />}
            disabled={locked}
            onClick={() => setDeleteOpen(true)}
          >
            Eliminar
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}

      {running && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            Rastreando webs y subiendo fotos a Drive…
          </Typography>
        </Box>
      )}

      {(() => {
        const c =
          search.ConstraintsJson && typeof search.ConstraintsJson === "object"
            ? search.ConstraintsJson
            : {};
        const brands = Array.isArray(c.researchBrands) ? c.researchBrands : [];
        const extras = Array.isArray(c.customUrls) ? c.customUrls : [];
        const perBrand = c.maxImagesPerBrand || 3;
        const ageDays = c.maxAgeDays != null ? c.maxAgeDays : 60;
        if (!brands.length && !extras.length) return null;
        return (
          <Box>
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.75,
                letterSpacing: 0.04,
                textTransform: "uppercase",
              }}
            >
              Fuentes · {perBrand} imgs/marca · novedades ≤{ageDays}d · sin
              duplicados · {brands.length + extras.length}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {brands.map((id) => (
                <Chip
                  key={id}
                  size="small"
                  label={id}
                  variant="outlined"
                  sx={{
                    height: 22,
                    borderRadius: 1.25,
                    fontSize: "0.68rem",
                    "& .MuiChip-label": { px: 0.85 },
                  }}
                />
              ))}
              {extras.map((u) => (
                <Chip
                  key={u}
                  size="small"
                  color="info"
                  variant="outlined"
                  label={String(u)
                    .replace(/^https?:\/\//, "")
                    .slice(0, 36)}
                  sx={{
                    height: 22,
                    borderRadius: 1.25,
                    fontSize: "0.68rem",
                    "& .MuiChip-label": { px: 0.85 },
                  }}
                />
              ))}
            </Stack>
          </Box>
        );
      })()}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
      >
        <Typography variant="subtitle2" fontWeight={800}>
          Fotos ({images.length})
        </Typography>
        {images.length > 0 && !running && (
          <Button
            size="small"
            color="warning"
            onClick={() => setClearOpen(true)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Borrar fotos
          </Button>
        )}
      </Stack>
      {images.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin fotos. Lanza para bajar solo novedades (~60 días). Relanzar suma
          solo lo nuevo; no vuelve a bajar URLs ya guardadas de este
          cliente/temporada.
        </Typography>
      ) : (
        (() => {
          const byBrand = new Map();
          for (const img of images) {
            const key = img.BrandName || img.BrandId || "Sin marca";
            if (!byBrand.has(key)) byBrand.set(key, []);
            byBrand.get(key).push(img);
          }
          return (
            <Stack spacing={2}>
              {[...byBrand.entries()].map(([brand, imgs]) => (
                <Box key={brand}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                    color="text.secondary"
                  >
                    {brand} · {imgs.length}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(140px, 1fr))",
                      gap: 1.25,
                    }}
                  >
                    {imgs.map((img) => (
                      <SearchThumb
                        key={img.id}
                        item={img}
                        onOpen={(it) => {
                          const idx = images.findIndex(
                            (x) => Number(x.id) === Number(it.id),
                          );
                          if (idx >= 0) setLightboxIndex(idx);
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          );
        })()
      )}

      {/* Edit params */}
      <Dialog
        open={editOpen}
        onClose={() => !busy && setEditOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Parámetros del SEARCH</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {[search.CustomerName, search.Temporada]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            <TextField
              size="small"
              label="Título"
              value={editForm.Titulo}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                label="Tipología"
                value={editForm.Tipologia}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, Tipologia: e.target.value }))
                }
                fullWidth
                sx={pageFieldSx}
              />
              <TextField
                size="small"
                label="Imgs/marca"
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={editForm.maxImagesPerBrand}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    maxImagesPerBrand: e.target.value,
                  }))
                }
                sx={{ width: 120, ...pageFieldSx }}
              />
              <TextField
                size="small"
                label="Novedades (días)"
                type="number"
                inputProps={{ min: 7, max: 365 }}
                value={editForm.maxAgeDays}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    maxAgeDays: e.target.value,
                  }))
                }
                helperText="Solo productos recientes"
                sx={{ width: 150, ...pageFieldSx }}
              />
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2" fontWeight={800}>
                Marcas ({editForm.brandIds.length}/{catalogBrands.length})
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() =>
                    setEditForm((p) => ({
                      ...p,
                      brandIds: catalogBrands.map((b) => b.id),
                    }))
                  }
                >
                  Todas
                </Button>
                <Button
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => setEditForm((p) => ({ ...p, brandIds: [] }))}
                >
                  Ninguna
                </Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                maxHeight: 240,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                px: 1,
              }}
            >
              {catalogBrands.map((b) => (
                <FormControlLabel
                  key={b.id}
                  sx={{ display: "flex", m: 0, py: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={editForm.brandIds.includes(b.id)}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setEditForm((p) => ({
                          ...p,
                          brandIds: on
                            ? [...new Set([...p.brandIds, b.id])]
                            : p.brandIds.filter((id) => id !== b.id),
                        }));
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {b.name}
                      </Typography>
                      {(b.urls || []).slice(0, 2).map((u) => (
                        <Typography
                          key={u}
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                        >
                          {u.replace(/^https?:\/\//, "")}
                        </Typography>
                      ))}
                    </Box>
                  }
                />
              ))}
            </Box>
            <TextField
              size="small"
              label="Webs extra"
              multiline
              minRows={2}
              value={editForm.customUrls}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, customUrls: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} disabled={busy}>
            Cerrar
          </Button>
          <Button
            disabled={busy}
            onClick={() => handleSaveParams({ andRun: false })}
          >
            Guardar
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={busy}
            onClick={() => handleSaveParams({ andRun: true })}
          >
            Guardar y relanzar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear photos confirm */}
      <Dialog
        open={clearOpen}
        onClose={() => !busy && setClearOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Borrar fotos</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Se quitarán las fotos de este SEARCH en el CRM y pasarán a la{" "}
            <b>papelera de Drive</b>. El SEARCH se queda para que puedas
            relanzar.
          </Typography>
          {images.length > 0 ? (
            <Typography variant="body2" sx={{ mt: 1.25 }} fontWeight={700}>
              {images.length} foto{images.length === 1 ? "" : "s"} a borrar
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={handleClearPhotos}
            startIcon={
              busy ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            Borrar fotos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Eliminar SEARCH</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se borrará del CRM y la carpeta/fotos de Drive irán a la{" "}
            <b>papelera</b>. ¿Seguro?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={handleDelete}
          >
            Eliminar todo
          </Button>
        </DialogActions>
      </Dialog>

      <SearchPhotoLightbox
        items={images}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </Stack>
  );
}
